/**
 * FH Announcement Bar Component
 * 
 * OOP Architecture:
 * - FHAnnouncementBar (Base): Core component initialization
 * - FHSliderMode: USP slider with auto-rotation
 * - FHMarqueeMode: Continuous scrolling animation (CSS-driven)
 * - FHStaticMode: No animation, all USPs visible
 * 
 * Design Principles:
 * - Strategy Pattern: Different display modes as strategies
 * - Single Responsibility: Each mode handler is independent
 * - Encapsulation: Private state management
 * - Responsive: Mode can differ between desktop and mobile
 */

// =============================================================================
// ANNOUNCEMENT BAR COMPONENT
// =============================================================================

class FHAnnouncementBar {
  /** @type {HTMLElement} */
  #container;
  /** @type {Object} */
  #config;
  /** @type {FHSliderMode|null} */
  #sliderMode = null;
  /** @type {AbortController} */
  #abortController;

  /**
   * @param {HTMLElement} container
   */
  constructor(container) {
    this.#container = container;
    this.#config = this.#parseConfig();
    this.#abortController = new AbortController();
    this.#initialize();
  }

  /**
   * Parse configuration from data attributes
   * @returns {Object}
   */
  #parseConfig() {
    const dataset = this.#container.dataset;
    return {
      sectionId: dataset.sectionId,
      sliderSpeed: parseInt(dataset.sliderSpeed) || 3000,
      desktopMode: dataset.desktopMode || 'static',
      mobileMode: dataset.mobileMode || 'slider',
      uspCount: parseInt(dataset.uspCount) || 0
    };
  }

  /**
   * Initialize component
   */
  #initialize() {
    // Only initialize slider if we have USPs
    if (this.#config.uspCount > 1) {
      this.#sliderMode = new FHSliderMode(
        this.#container,
        this.#config.sliderSpeed
      );
    }

    // Setup responsive mode switching
    this.#setupResponsiveMode();

    // Setup hover pause
    this.#setupHoverPause();
  }

  /**
   * Setup responsive mode changes
   */
  #setupResponsiveMode() {
    const signal = this.#abortController.signal;
    let resizeTimeout;

    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.#updateMode();
      }, 150);
    };

    window.addEventListener('resize', handleResize, { signal });
    
    // Initial mode setup
    this.#updateMode();
  }

  /**
   * Update display mode based on viewport
   */
  #updateMode() {
    const isMobile = window.innerWidth < 768;
    const currentMode = isMobile ? this.#config.mobileMode : this.#config.desktopMode;
    
    if (currentMode === 'slider' && this.#sliderMode) {
      this.#sliderMode.start();
    } else {
      this.#sliderMode?.stop();
    }
  }

  /**
   * Setup hover pause for slider
   */
  #setupHoverPause() {
    const signal = this.#abortController.signal;

    this.#container.addEventListener('mouseenter', () => {
      if (this.#shouldUseSlider()) {
        this.#sliderMode?.pause();
      }
    }, { signal });

    this.#container.addEventListener('mouseleave', () => {
      if (this.#shouldUseSlider()) {
        this.#sliderMode?.resume();
      }
    }, { signal });
  }

  /**
   * Check if current mode is slider
   * @returns {boolean}
   */
  #shouldUseSlider() {
    const isMobile = window.innerWidth < 768;
    const currentMode = isMobile ? this.#config.mobileMode : this.#config.desktopMode;
    return currentMode === 'slider';
  }

  /**
   * Destroy component
   */
  destroy() {
    this.#sliderMode?.stop();
    this.#abortController.abort();
  }

  /**
   * Get configuration
   * @returns {Object}
   */
  getConfig() {
    return { ...this.#config };
  }
}

// =============================================================================
// SLIDER MODE (Strategy)
// =============================================================================

class FHSliderMode {
  /** @type {HTMLElement} */
  #container;
  /** @type {NodeList} */
  #usps;
  /** @type {number} */
  #speed;
  /** @type {number} */
  #currentIndex = 0;
  /** @type {number|null} */
  #intervalId = null;
  /** @type {boolean} */
  #isPaused = false;

  /**
   * @param {HTMLElement} container
   * @param {number} speed - Interval in ms
   */
  constructor(container, speed) {
    this.#container = container;
    this.#speed = speed;
    this.#usps = container.querySelectorAll('.fh-announcement-bar__usp:not([data-duplicate])');
  }

  /**
   * Start slider
   */
  start() {
    if (this.#intervalId) return;
    if (this.#usps.length <= 1) return;

    // Show first USP
    this.#showUSP(0);

    this.#intervalId = setInterval(() => {
      if (!this.#isPaused) {
        this.#next();
      }
    }, this.#speed);
  }

  /**
   * Stop slider
   */
  stop() {
    if (this.#intervalId) {
      clearInterval(this.#intervalId);
      this.#intervalId = null;
    }

    // Remove active states
    this.#usps.forEach(usp => usp.classList.remove('is-active'));
  }

  /**
   * Pause slider (keeps interval but doesn't advance)
   */
  pause() {
    this.#isPaused = true;
  }

  /**
   * Resume slider
   */
  resume() {
    this.#isPaused = false;
  }

  /**
   * Go to next USP
   */
  #next() {
    this.#currentIndex = (this.#currentIndex + 1) % this.#usps.length;
    this.#showUSP(this.#currentIndex);
  }

  /**
   * Show specific USP
   * @param {number} index
   */
  #showUSP(index) {
    this.#usps.forEach((usp, i) => {
      usp.classList.toggle('is-active', i === index);
    });
  }
}

// =============================================================================
// REGISTRY & INITIALIZATION
// =============================================================================

const FHAnnouncementBarRegistry = {
  /** @type {Map<string, FHAnnouncementBar>} */
  instances: new Map(),

  /**
   * Initialize announcement bar
   * @param {HTMLElement} element
   * @returns {FHAnnouncementBar}
   */
  init(element) {
    const id = element.dataset.sectionId;
    
    // Remove existing
    this.destroy(id);

    const instance = new FHAnnouncementBar(element);
    this.instances.set(id, instance);
    return instance;
  },

  /**
   * Destroy instance
   * @param {string} id
   */
  destroy(id) {
    const instance = this.instances.get(id);
    if (instance) {
      instance.destroy();
      this.instances.delete(id);
    }
  },

  /**
   * Initialize all announcement bars
   */
  initAll() {
    document.querySelectorAll('[data-section-type="fh-announcement-bar"]').forEach(element => {
      this.init(element);
    });
  }
};

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  FHAnnouncementBarRegistry.initAll();
});

// Shopify Theme Editor support
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const bar = event.target.querySelector('[data-section-type="fh-announcement-bar"]');
    if (bar) {
      FHAnnouncementBarRegistry.init(bar);
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const bar = event.target.querySelector('[data-section-type="fh-announcement-bar"]');
    if (bar) {
      FHAnnouncementBarRegistry.destroy(bar.dataset.sectionId);
    }
  });
}
